import { useCallback, useEffect, useRef, useState } from "react";
import { FONT_FAMILY_SET } from "@/fonts/catalog";

const RECENT_FONTS_STORAGE_KEY = "wmRecentFonts";
const MAX_RECENT_FONTS = 4;

function getFilteredRecentFonts(value: unknown): string[] {
    if (!Array.isArray(value)) {
        return [];
    }

    const normalized = value.filter(
        (font): font is string => typeof font === "string" && FONT_FAMILY_SET.has(font),
    );

    return [...new Set(normalized)].slice(0, MAX_RECENT_FONTS);
}

function updateRecentFonts(prevFonts: string[], fontFamily: string): string[] {
    return [fontFamily, ...prevFonts.filter((font) => font !== fontFamily)].slice(
        0,
        MAX_RECENT_FONTS,
    );
}

export function useRecentTextFonts() {
    const [recentFonts, setRecentFonts] = useState<string[]>([]);
    const recentFontsRef = useRef<string[]>([]);

    useEffect(() => {
        try {
            const rawRecentFonts = localStorage.getItem(RECENT_FONTS_STORAGE_KEY);
            if (!rawRecentFonts) {
                return;
            }

            const parsed = JSON.parse(rawRecentFonts);
            const filteredFonts = getFilteredRecentFonts(parsed);
            recentFontsRef.current = filteredFonts;
            setRecentFonts(filteredFonts);
        } catch {
            recentFontsRef.current = [];
            setRecentFonts([]);
        }
    }, []);

    const addRecentFont = useCallback((fontFamily: string) => {
        const nextRecentFonts = updateRecentFonts(recentFontsRef.current, fontFamily);
        recentFontsRef.current = nextRecentFonts;

        setRecentFonts(nextRecentFonts);

        try {
            localStorage.setItem(RECENT_FONTS_STORAGE_KEY, JSON.stringify(nextRecentFonts));
        } catch {
            // Не блокируем применение шрифта при проблемах со storage.
        }
    }, []);

    return { recentFonts, addRecentFont };
}
